/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react'

import {
  PIICategoryParameterFormats,
  addOrUpdatePIICategoryFormat,
} from '../../../assets/ts/data/pii/pii-category-parameter-formats'
import { defaultToEmptyString } from '../../../assets/ts/formatting/formatting'
import { convertBooleansToYesNo } from '../../../assets/ts/general/general'
import Buttons from '../../../components/buttons/buttons'
import PageOverlayBusy from '../../../components/page-overlay/page-overlay-busy'
import '../../../components/table/table.css'
import usePIICategoryParameterFormatsMetadata from '../../../hooks/pii/use-pii-category-parameter-format-metadata'
import InputCheckbox from '../../../utils/controls/input-checkbox'
import InputText from '../../../utils/controls/input-text'

interface Props {
  categoryIsEnabled?: boolean // Functionally needed if we ever want to toggle all items. 
  parentId: number | undefined
}
const ConfigurationFormat = ({ categoryIsEnabled, parentId }: Props) => {
  const [editingId, setEditingId] = useState(-1)

  const [showFormatDropDown, setShowFormatDropDown] = useState(false)
  const [showPageOverlay, setShowPageOverlay] = useState(false)

  const [inEditModeFormat, setInEditModeFormat] = useState({
    rowKey: null,
    status: false,
  })
  const [addNewFormat, setAddNewFormat] = useState<PIICategoryParameterFormats>({
    Format: '',
    FormatEnable: 'No',
    FormatExample: '',
    FormatRegExp: '',
    PID: parentId,
    Title: '',
    eKey: '',
    eTag: '',
  })
  const [displayError, setDisplayError] = useState({
    showErrorFormat: false,
    showErrorRegex: false,
    showInvalidErrEdit: false,
    showInvalidRegexErr: false,
  })
  const [editFormats, setEditFormats] = useState<PIICategoryParameterFormats[]>([
    {
      Format: '',
      FormatEnable: '',
      FormatExample: '',
      FormatRegExp: '',
      PID: parentId,
      Title: '',
      eKey: '',
      eTag: '',
    },
  ])
  const { fetchPiiCategoryParameterFormatsMetadata, piiCategoryParameterFormatsMetadata } =
    usePIICategoryParameterFormatsMetadata({
      isArchivedFilter: null,
      parentIdFilter: -1,
    })

  const handleAddFormatChange = (column: string, value: boolean | string): void => {
    const newValue = typeof value === 'boolean' ? convertBooleansToYesNo(value) : value
    setAddNewFormat((prevFormat) => {
      const updatedFormat = {
        ...prevFormat,
        [column]: newValue,
      }
      // If the column is 'Format', also update the 'Title'
      if (column === 'Format') {
        updatedFormat.Title = newValue
      }
      return updatedFormat
    })
  }

  const handleEditFormatChange = (index, column: string, value): void => {
    // Ensure the index is within bounds
    if (index < 0 || index >= editFormats.length) {
      console.error(`Index ${index} out of bounds for editFormats array`)
      return
    }

    // Create a new array with updated values
    const newEditFormats = [...editFormats]
    newEditFormats[index] = {
      ...newEditFormats[index],
      [column]: value,
    }

    // Update the state
    setEditFormats(newEditFormats)
  }

  const toggleEnable = async (index, column: string, value: 'No' | 'Yes'): Promise<void> => {
    // Ensure the index is within bounds
    if (index < 0 || index >= editFormats.length) {
      console.error(`Index ${index} out of bounds for editFormats array`)
      return
    }
    setShowPageOverlay(true)

    // Create a new array with updated values
    const newEditFormats = [...editFormats]
    newEditFormats[index] = {
      ...newEditFormats[index],
      [column]: value,
    }
    await addOrUpdatePIICategoryFormat(newEditFormats[index])
    fetchPiiCategoryParameterFormatsMetadata()
    setShowPageOverlay(false)
  }

  const addOrUpdateNewFormat = async (
    id,
    data: PIICategoryParameterFormats,
    type: 'ADD' | 'UPDATE'
  ) => {
    setShowPageOverlay(true)

    // Initialize display error states
    setDisplayError((prev) => ({
      ...prev,
      showErrorFormat: false,
      showErrorRegex: false,
      showInvalidRegexErr: false,
    }))
    let validRegex = true
    try {
      new RegExp('(' + data.FormatRegExp + ')')
    } catch (e) {
      validRegex = false
    }

    const isTitleValid = data.Format?.trim() !== ''
    const isFormatRegExpValid = data.FormatRegExp?.trim() !== ''

    if (isTitleValid && isFormatRegExpValid && validRegex) {
      const payload =
        type === 'UPDATE'
          ? { ...data, Format: data.Format, eKey: data.eKey, eTag: data.eTag }
          : data
      await addOrUpdatePIICategoryFormat(payload)

      if (type === 'ADD') {
        setAddNewFormat({
          Format: '',
          FormatEnable: 'No',
          FormatExample: '',
          FormatRegExp: '',
          PID: data.PID,
          Title: '',
        })
      }

      if (type === 'UPDATE') {
        setInEditModeFormat({
          rowKey: id,
          status: inEditModeFormat.rowKey === id ? !inEditModeFormat.status : true,
        })
      }

      // Reset error states and other states
      setDisplayError({
        ...displayError,
        showErrorFormat: false,
        showErrorRegex: false,
        showInvalidRegexErr: false,
      })
      setShowFormatDropDown(false)
      setEditingId(-1)
      fetchPiiCategoryParameterFormatsMetadata()
    } else {
      // Log error messages for debugging and update error states
      if (!validRegex) {
        setDisplayError((prev) => ({
          ...prev,
          showInvalidRegexErr: true,
        }))
      }
      if (!isTitleValid) {
        setDisplayError((prev) => ({
          ...prev,
          showErrorFormat: true,
        }))
      }
      if (!isFormatRegExpValid) {
        setDisplayError((prev) => ({
          ...prev,
          showErrorRegex: true,
        }))
      }
    }

    setShowPageOverlay(false)
  }

  const handleEdit = (id): void => {
    setEditingId(id)
    setInEditModeFormat({
      rowKey: id,
      status: inEditModeFormat.rowKey === id ? !inEditModeFormat.status : true,
    })
  }

  const toggleNewFormat = (): void => {
    setShowFormatDropDown(!showFormatDropDown)
    setDisplayError({
      ...displayError,
      showErrorFormat: false,
      showErrorRegex: false,
      showInvalidErrEdit: false,
      showInvalidRegexErr: false,
    })
  }

  const handleCancelFormat = (): void => {
    setEditingId(-1)
    setDisplayError({
      ...displayError,
      showErrorFormat: false,
      showErrorRegex: false,
      showInvalidErrEdit: false,
      showInvalidRegexErr: false,
    })
    setShowFormatDropDown(!showFormatDropDown)
    fetchPiiCategoryParameterFormatsMetadata()
  }

  const handleCancelEditFormat = (): void => {
    setEditingId(-1)
    setDisplayError({
      ...displayError,
      showErrorFormat: false,
      showErrorRegex: false,
      showInvalidErrEdit: false,
      showInvalidRegexErr: false,
    })
    setInEditModeFormat({
      rowKey: null,
      status: false,
    })
    fetchPiiCategoryParameterFormatsMetadata()
  }

  useEffect(() => {
    setEditFormats(piiCategoryParameterFormatsMetadata)
  }, [piiCategoryParameterFormatsMetadata])

  useEffect(() => {
    fetchPiiCategoryParameterFormatsMetadata()
  }, [categoryIsEnabled])

  return (
    <>
      {showPageOverlay && <PageOverlayBusy />}
      <div className='w-100 overflow-auto'>
        <table className='table-borderless w-100 sticky-top first-2column last-column piitable'>
          <thead className='border-bottom2'>
            <tr>
              <th className='py-1' style={{ width: 40 }}></th>
              <th aria-label='Format' className='py-1' tabIndex={0}>
                Format
              </th>
              <th aria-label='Example' className='py-1' tabIndex={0}>
                Example
              </th>
              <th aria-label='Regular Expression' className='py-1' tabIndex={0}>
                Regular Expression
              </th>
              <th className='py-1' tabIndex={0}>
                <Buttons
                  aria-label='New Format'
                  className='btn-xs font-12 title-color36 py-1 uppercase text-nowrap border-radius03 ms-auto bgcolor-41 bordered5 poppins-medium '
                  icon='icon-add me-1 font-10'
                  label='New Format'
                  onClick={() => toggleNewFormat()}
                />
              </th>
            </tr>
          </thead>
          <tbody>
            {showFormatDropDown && (
              <tr>
                <th>
                  <InputCheckbox
                    formClassName='form-horizontal font-0'
                    inputProps={{
                      checked: addNewFormat.FormatEnable === 'Yes',
                      className: 'font-0 poppins-medium title-color',
                      id: 'inputcheck',
                      label: 'check',
                      name: 'Input Check',
                      type: 'checkbox',
                      value: addNewFormat.FormatEnable,
                    }}
                    onChange={(e) =>
                      handleAddFormatChange(
                        'FormatEnable',
                        addNewFormat.FormatEnable !== 'Yes' ? true : false
                      )
                    }
                  />
                </th>
                <th>
                  <div className='d-flex'>
                    <InputText
                      aria-label='Format'
                      changeHandler={(e) => handleAddFormatChange('Format', e.target.value)}
                      className='form-sm font-13 sourcesanspro'
                      error={displayError.showErrorFormat}
                      formClassName='form-horizontal w-100 flex-wrap '
                      inputProps={{
                        className: 'font-12 poppins-medium title-color',
                        id: 'Format',
                        name: 'newformat',
                        placeholder: 'Enter Format',
                      }}
                      isMandatory
                      msg='Please enter Format.'
                      value={addNewFormat.Format}
                    />
                    <span className='mandatory ms-1'>*</span>
                  </div>
                </th>
                <td className='v-align-top'>
                  <InputText
                    aria-label='Example'
                    changeHandler={(e) => handleAddFormatChange('FormatExample', e.target.value)}
                    className='form-sm font-13 sourcesanspro'
                    formClassName='form-horizontal'
                    inputProps={{
                      className: 'font-12 poppins-medium title-color',
                      id: 'newformat',
                      name: 'newformat',
                      placeholder: 'Enter Example',
                    }}
                    isMandatory
                    value={addNewFormat.FormatExample}
                  />
                </td>
                <td>
                  <div className='d-flex'>
                    <InputText
                      aria-label='Regular Expression'
                      changeHandler={(e) => handleAddFormatChange('FormatRegExp', e.target.value)}
                      className='form-sm font-13 sourcesanspro'
                      error={displayError.showErrorRegex}
                      formClassName='form-horizontal w-100 flex-wrap '
                      inputProps={{
                        className: 'font-12 poppins-medium title-color',
                        id: 'newregularexpression',
                        name: 'newregularexpression',
                        placeholder: 'Enter Regular Expression',
                      }}
                      isMandatory
                      msg='Please enter Regular Expression.'
                      value={addNewFormat.FormatRegExp}
                    />
                    <span className='mandatory ms-1'>*</span>
                  </div>
                </td>
                <th>
                  <div className='d-flex gap-1'>
                    <Buttons
                      aria-label='Save'
                      className='btn-xs font-0 btn-border text-white bgcolor-primary border-radius ms-auto'
                      icon='icon-checked font-11'
                      label='Save'
                      onClick={(e) =>
                        addOrUpdateNewFormat(
                          null,
                          {
                            Format: addNewFormat.Format,
                            FormatEnable: addNewFormat.FormatEnable,
                            FormatExample: addNewFormat.FormatExample,
                            FormatRegExp: addNewFormat.FormatRegExp,
                            PID: addNewFormat.PID,
                            Title: addNewFormat.Title,
                          },
                          'ADD'
                        )
                      }
                    />

                    <Buttons
                      aria-label='Cancel'
                      className='btn-xs font-0 btn-border2 bgcolor-5 darktext  border-radius'
                      icon='icon-close font-10'
                      label='Cancel'
                      onClick={() => handleCancelFormat()}
                    />
                  </div>
                </th>
              </tr>
            )}
            {editFormats.map((format, index) => {
              if (format.PID === parentId) {
                const isEditing =
                  inEditModeFormat.status && inEditModeFormat.rowKey === `${format.PID}-${index}`
                if (isEditing) {
                  return (
                    <tr key={`${format.PID}-${index}`}>
                      <th className='checkbox'>
                        <InputCheckbox
                          formClassName='form-horizontal font-0'
                          inputProps={{
                            checked: format.FormatEnable === 'Yes',
                            id: format.PID,
                            label: format.Title,
                            name: 'Input Check',
                            type: 'checkbox',
                            value: format.FormatEnable,
                          }}
                          onChange={(e) =>
                            handleEditFormatChange(
                              index,
                              'FormatEnable',
                              format.FormatEnable !== 'Yes' ? 'Yes' : 'No'
                            )
                          }
                        />
                      </th>
                      <th className='col-sm-3'>
                        <input
                          className='form-xs'
                          onChange={(e) => handleEditFormatChange(index, 'Format', e.target.value)}
                          type='text'
                          value={defaultToEmptyString(format.Format)}
                        />
                        {displayError.showErrorFormat && (
                          <span className='errormsg'>Please enter Format.</span>
                        )}
                        <span className='mandatory ms-1'>*</span>
                      </th>
                      <td className='col-sm-3'>
                        <input
                          className='form-xs'
                          onChange={(e) =>
                            handleEditFormatChange(index, 'FormatExample', e.target.value)
                          }
                          type='text'
                          value={format.FormatExample}
                        />
                      </td>
                      <td className='col-sm'>
                        <input
                          className='form-xs'
                          onChange={(e) =>
                            handleEditFormatChange(index, 'FormatRegExp', e.target.value)
                          }
                          type='text'
                          value={format.FormatRegExp}
                        />
                        {displayError.showErrorRegex && (
                          <span className='errormsg'>Please enter Regular Expression.</span>
                        )}
                        <span className='mandatory ms-1'>*</span>
                      </td>
                      <th className='col-sm-2 col-md-3'>
                        <div className='d-flex gap-1'>
                          <Buttons
                            aria-label='Save'
                            className='btn-xs font-0 btn-border text-white bgcolor-primary border-radius ms-auto'
                            icon='icon-checked font-11'
                            label='Save'
                            onClick={(e) =>
                              addOrUpdateNewFormat(
                                `${format.PID}-${index}`,
                                editFormats[index],
                                'UPDATE'
                              )
                            }
                          />
                          <Buttons
                            aria-label='Cancel'
                            className='btn-xs font-0 btn-border2 bgcolor-5 darktext border-radius'
                            icon='icon-close font-10'
                            label='Cancel'
                            onClick={() => handleCancelEditFormat()}
                          />
                        </div>
                      </th>
                    </tr>
                  )
                } else {
                  return (
                    <tr key={`${format.PID}-${index}`}>
                      <th className='checkbox'>
                        <InputCheckbox
                          formClassName='form-horizontal font-0'
                          inputProps={{
                            checked: format.FormatEnable === 'Yes',
                            id: format.PID,
                            label: format.Title,
                            name: 'Input Check',
                            type: 'checkbox',
                            value: format.FormatEnable,
                          }}
                          onChange={(e) =>
                            toggleEnable(
                              index,
                              'FormatEnable',
                              format.FormatEnable !== 'Yes' ? 'Yes' : 'No'
                            )
                          }
                        />
                      </th>
                      <th>{defaultToEmptyString(format.Format)}</th>
                      <td>{defaultToEmptyString(format.FormatExample)}</td>
                      <td>{defaultToEmptyString(format.FormatRegExp)}</td>
                      <th>
                        <div className='ms-auto d-flex gap-1'>
                          <Buttons
                            aria-label='Edit'
                            className='btn-xs font-0 color-primary editbtn ms-auto me-2'
                            icon='icon-pencil font-14'
                            label='Edit'
                            onClick={() => handleEdit(`${format.PID}-${index}`)}
                          />
                        </div>
                      </th>
                    </tr>
                  )
                }
              }
            })}
          </tbody>
        </table>
      </div>
    </>
  )
}
export default ConfigurationFormat
